import ClassNames from 'classnames'
import { getMergedFilterParams, getMergedFilterQueryString } from 'core/queryString'
import * as React from 'react'
import * as constants from 'static/constants'
import styled from 'styled-components'
import I18n from '../../../core/i18n'
import { IJsonResponse } from '../../../core/JsonApiSerializer'
import { adminService, postService } from '../../../core/services'
import * as interfaces from '../../../static/interfaces'
import * as Hooks from '../../../utils/hooks'
import { Button, DateFilter, GuestFilter, PanelTitle, PriceFilter, TypeFilter } from '../../atoms'
import { EditTable } from '../../organisms'
import AdminLayoutIndex from './_layouts/'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow

interface IProps {
  posts: {
    posts: IJsonResponse
  }
  tags: IJsonResponse
  languages: IJsonResponse
  categories: IJsonResponse
  areas: IJsonResponse
}

const COLUMNS = [
  {
    name: 'ID',
    field: 'id',
    required: false,
    readOnly: true,
  },
  {
    name: I18n.t('generic.status'),
    field: 'aasm_state_i18n',
    required: false,
  },
  {
    name: I18n.t('generic.area'),
    field: 'area',
    required: false,
  },
  {
    name: I18n.t('generic.name'),
    field: 'name',
    required: true,
  },
  {
    name: I18n.t('generic.host'),
    field: 'host',
    required: true,
    renderElement: record => {
      return (
        <a href={`/admin/users/${record.host.id}/edit`} target="blank">
          {record.host.fullname}
        </a>
      )
    },
  },
  {
    name: I18n.t('post.language'),
    field: 'language',
    required: true,
  },
  {
    name: I18n.t('generic.price'),
    field: 'price',
    required: true,
  },
  {
    name: I18n.t('generic.publication_date'),
    field: 'published_at',
    required: false,
  },
]

const formatPosts = data =>
  data.map(record => ({
    id: record.id,
    slug: record.slug,
    aasm_state: record.aasm_state,
    aasm_state_i18n: record.aasm_state_i18n,
    area: record?.area?.name || '',
    name: record.name,
    host: record.user,
    language: record.languages.map(lang => lang.name).join(','),
    price: record.price,
    published_at: record.published_at,
  }))

const urlParams = new URLSearchParams(window.location.search)
const FILTER_FIELDS = [
  'date',
  'min_price',
  'max_price',
  'guests',
  'category_ids',
  'area_ids',
  'tag_ids',
  'post_types',
  'language_ids',
]

const searchPost = async baseParams => {
  const keywords = urlParams.get('keywords')
  const params = { ...baseParams }

  if (keywords) {
    params.keywords = keywords
  }

  return adminService.searchPost(params)
}

const Index: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle('Admin post')
  const { data } = adminService.getDataFromJson(props.posts.posts)
  const [posts, setPosts] = React.useState(formatPosts(data))
  const { pagination: initialPagination } = React.useMemo(
    () => postService.getPostsFromJson(props.posts.posts),
    []
  )
  const [loading, setLoading] = React.useState(false)
  const [pagination, setPagination] = React.useState(initialPagination)

  const getPostsByPage = React.useCallback(async page => {
    setLoading(true)
    const currentSearchParams = new URLSearchParams(window.location.search)
    const filterParams = {}
    currentSearchParams.forEach((val, key) => {
      filterParams[key] = val
    })
    const result = await searchPost({ ...filterParams, page })

    setPosts(formatPosts(result.posts))
    setPagination(result.pagination)
    setLoading(false)
  }, [])

  const handleClickDelete = record => {
    if (window.confirm(I18n.t('generic.delete_confirmation'))) {
      handleDelete(record)
    }
  }

  const handleDelete = React.useCallback(
    async id => {
      const response = await adminService.deletePost(id)
      const flush = response.data.flush
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      setPosts(posts.filter(item => item.id !== id))
      setPagination(response.data.posts.posts.meta)
    },
    [posts]
  )

  const renderRecordControls = React.useCallback(
    record => (
      <>
        <Button key="view" small={true}>
          <a href={`/posts/${record.slug}`}>{I18n.t('generic.view')}</a>
        </Button>
      </>
    ),
    []
  )

  const handleOnFilterSubmit = React.useCallback(
    async filterValue => {
      setLoading(true)

      const filterParams = getMergedFilterParams(FILTER_FIELDS, filterValue)
      const filterQueryString = getMergedFilterQueryString(FILTER_FIELDS, filterParams)
      const result = await searchPost({ ...filterParams })

      setPosts(formatPosts(result.posts))
      setPagination(result.pagination)
      setLoading(false)
      history.replaceState(null, null, `?${filterQueryString}`)
    },
    [posts]
  )

  return (
    <AdminLayoutIndex
      model="post"
      title={<PanelTitle title={I18n.t('generic.posts')} />}
      main={
        <>
          <div>
            <SearchHeader
              handleOnFilterSubmit={handleOnFilterSubmit}
              categories={props.categories}
              areas={props.areas}
              tags={props.tags}
              languages={props.languages}
            />
          </div>
          <EditTable
            getResourceByPage={getPostsByPage}
            isCustomResource={true}
            loading={loading}
            editable={true}
            columns={COLUMNS}
            records={posts}
            pagination={pagination}
            handleDelete={handleClickDelete}
            renderRecordControls={renderRecordControls}
          />
        </>
      }
    />
  )
}

interface ISearchHeaderProps {
  categories: IJsonResponse
  areas: IJsonResponse
  tags: IJsonResponse
  languages: IJsonResponse
  handleOnFilterSubmit(params: any): void
}

const POST_TYPES = [
  {
    id: 'private',
    name: 'Private',
  },
  {
    id: 'group',
    name: 'Group',
  },
]

const ENTER_KEYCODE = 13

const SearchHeader: React.FC<ISearchHeaderProps> = props => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState(false)
  const { data: tags } = postService.getDataFromJson(props.tags)
  const { data: languages } = postService.getDataFromJson(props.languages)
  const { data: areas } = postService.getDataFromJson(props.areas)
  const { data: categories } = postService.getDataFromJson(props.categories)
  const defaultKeywords = urlParams.get('keywords') || ''
  const onSearchHandler = async event => {
    const currentUrlParams = new URLSearchParams(window.location.search)

    if (event.target.value) {
      currentUrlParams.set(event.target.name, event.target.value)
    } else {
      currentUrlParams.delete(event.target.name)
    }

    location.href = `posts?${currentUrlParams.toString()}`
  }

  const onKeyDownHandler = event => {
    if (event.keyCode && event.keyCode === ENTER_KEYCODE) {
      onSearchHandler(event)
    }
  }

  return (
    <SearchHeaderWrapper className={ClassNames('SearchHeader', { isFilterPanelOpen })}>
      <Search className="Search">
        <div className="Search_Input _keywords">
          <input
            name="keywords"
            type="text"
            placeholder={I18n.t('generic.keyword')}
            defaultValue={defaultKeywords}
            onBlur={onSearchHandler}
            onKeyDown={onKeyDownHandler}
          />
          <SvgIcon name="search" className="icon" ariaHidden={true} role="presentation" />
        </div>
      </Search>
      <FilterList>
        <TypeFilter
          name={I18n.t('generic.area')}
          field="area_ids"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
          types={areas}
        />
        <DateFilter
          name={I18n.t('date')}
          field="date"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
        />
        <GuestFilter
          name={I18n.t('generic.guest')}
          field="guests"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
        />
        {constants.PAYMENT_REQUIRED && (
          <PriceFilter
            name={I18n.t('generic.price')}
            field="price"
            setIsFilterPanelOpen={setIsFilterPanelOpen}
            handleOnSubmit={props.handleOnFilterSubmit}
          />
        )}
        <TypeFilter
          name={I18n.t('generic.type')}
          field="post_types"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
          types={POST_TYPES}
        />
        <TypeFilter
          name={I18n.t('generic.tag')}
          field="tag_ids"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
          types={tags}
        />
        <TypeFilter
          name={I18n.t('generic.category')}
          field="category_ids"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
          types={categories}
        />
        <TypeFilter
          name={I18n.t('post.language')}
          field="language_ids"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
          types={languages}
        />
      </FilterList>
    </SearchHeaderWrapper>
  )
}

const SearchHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
  border-bottom: none;
  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    ::-webkit-scrollbar {
      display: none;
    }
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    height: auto;
    padding: 6px 12px;
  }

  &.isFilterPanelOpen {
    overflow: visible;
  }
`

const FilterList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: calc(100% - 200px);
  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    width: 100%;
  }
  > div {
    display: inline-block;
  }
`
const Search = styled.div`
  display: flex;
  position: relative;
  width: 360px;
  margin-right: 10px;

  .Search_Input {
    position: relative;
    height: 36px;

    > .icon {
      position: absolute;
      top: 8px;
      left: 8px;
      margin: auto;
      width: 24px;
      height: 24px;
    }

    > input {
      display: block;
      width: 100%;
      padding: 8px 12px 8px 36px;
      border: solid 1px ${constants.COLORS.divider};
      border-radius: 4px;
      font-size: 15px;
      transition: border 0.2s ease;
      outline: none;
      background: none;

      &::placeholder {
        color: ${constants.COLORS.subText};
      }

      &:focus {
        border: solid 1px ${constants.COLORS.primary};
      }
    }
  }
`
export default Index
